import { Banner, Button, ButtonGroup, LegacyStack, Modal, Text, TextContainer } from '@shopify/polaris';
import DropdownButton from '../Common/DropdownButton';
import { filterPermissionsMenu } from '../../../services/permissionsService';
import { useContext, useEffect, useMemo, useState } from 'react';
import { PDFModal } from '../Modals/PDFModal';
import { dateToHumanReadableFormat } from '../../../helpers/dateConverter';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import ToastContext from '../../../context/ToastContext';
import { useInventoryActionMutation } from '../../../customHooks/useInventoryQueries';

// Configuración por acción: copy del modal, etiqueta del dropdown, variante
// visual y permiso requerido. Replica la idea de `actionConfig` del staff
// (mirtomex-staff/src/pages/InventarioPorId.tsx) para mantener consistencia
// entre las dos UIs.
const ACTION_CONFIG = {
  START: {
    label: 'Iniciar inventario',
    title: 'Comenzar inventario',
    description: (id) =>
      `Vas a iniciar el inventario #${id}. Una vez iniciado, podrás empezar a contar productos.`,
    confirmText: 'Comenzar',
    destructive: false,
    permissions: ['INVENTORY:UPDATE'],
  },
  REVIEW: {
    label: 'Poner en revisión',
    title: 'Pasar a revisión',
    description: (id) =>
      `El inventario #${id} pasará a estado de revisión. Podrás seguir añadiendo productos mientras verificas las diferencias.`,
    confirmText: 'Pasar a revisión',
    destructive: false,
    permissions: ['INVENTORY:UPDATE'],
  },
  CLOSE: {
    label: 'Cerrar inventario',
    title: 'Cerrar inventario',
    description: (id) =>
      `Vas a cerrar el inventario #${id}. Una vez cerrado no se podrán añadir más productos y las diferencias se aplicarán al stock del depósito.`,
    warning: 'Esta acción es irreversible y modificará el stock del depósito.',
    confirmText: 'Cerrar inventario',
    destructive: false,
    permissions: ['INVENTORY:CLOSE'],
  },
  CANCEL: {
    label: 'Cancelar inventario',
    title: 'Cancelar inventario',
    description: (id) => `Estás a punto de cancelar el inventario #${id}. Todo el progreso se perderá.`,
    warning: 'Esta acción es irreversible.',
    confirmText: 'Cancelar inventario',
    destructive: true,
    permissions: ['INVENTORY:UPDATE'],
  },
  SCHEDULE: {
    label: 'Programar inventario',
    title: 'Programar inventario',
    description: (id) => `Programar el inventario #${id} para una fecha futura.`,
    confirmText: 'Programar',
    destructive: false,
    permissions: ['INVENTORY:UPDATE'],
  },
};

const ViewInventoryPrimaryActions = ({ inventory }) => {
  const [, dispatchToast] = useContext(ToastContext);

  const mutateAction = useInventoryActionMutation(inventory?.deposit?.id, inventory?.id);

  // Acción seleccionada y pendiente de confirmación. null = sin modal abierto.
  const [pendingAction, setPendingAction] = useState(null);

  const [activePDFModal, setActivePDFModal] = useState(false);
  const [queryPDF, setQueryPDF] = useState({ inventoryId: inventory?.id });

  const handlePDFExport = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const q = {};
    for (const [key, value] of urlParams) q[key] = value;
    delete q['r'];
    setQueryPDF({ ...queryPDF, ...q });
    setActivePDFModal(true);
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    const action = pendingAction;
    mutateAction.mutate(action, {
      onSuccess: (r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        setPendingAction(null);
      },
      onError: (e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response?.data || { message: 'Error al ejecutar la acción' } });
        setPendingAction(null);
      },
    });
  };

  const handleCloseModal = () => {
    if (mutateAction.isLoading) return; // no cerrar mientras está ejecutando
    setPendingAction(null);
  };

  // Construir items del dropdown a partir de inventory.actions (la lista que
  // devuelve el back con las transiciones válidas desde el estado actual).
  // El back es la fuente de verdad: aquí no se decide qué se muestra, solo
  // cómo se muestra.
  const dropdownItems = useMemo(() => {
    if (!inventory?.actions?.length) return [];
    return inventory.actions
      .filter((a) => ACTION_CONFIG[a])
      .map((a) => ({
        content: ACTION_CONFIG[a].label,
        onAction: () => setPendingAction(a),
        destructive: ACTION_CONFIG[a].destructive,
        permissions: ACTION_CONFIG[a].permissions,
      }));
  }, [inventory?.actions]);

  const dropdownItemsExport = [
    { content: 'Exportar a PDF', disabled: false, onAction: handlePDFExport },
    { content: 'Exportar a CSV', disabled: true },
  ];

  if (!inventory) return null;

  const config = pendingAction ? ACTION_CONFIG[pendingAction] : null;

  return (
    <LegacyStack>
      <DropdownButton
        title={'Exportar'}
        items={dropdownItemsExport}
        buttonProps={{ disabled: dropdownItemsExport.every((i) => i.disabled) }}
      />
      {dropdownItems.length > 0 && (
        <DropdownButton title={'Acciones'} items={filterPermissionsMenu(dropdownItems)} buttonProps={{}} />
      )}

      {config && (
        <Modal
          open={Boolean(pendingAction)}
          onClose={handleCloseModal}
          title={config.title}
          primaryAction={{
            content: config.confirmText,
            destructive: config.destructive,
            onAction: handleConfirm,
            loading: mutateAction.isLoading,
          }}
          secondaryActions={[{ content: 'Cancelar', onAction: handleCloseModal, disabled: mutateAction.isLoading }]}
        >
          <Modal.Section>
            <TextContainer>
              <p>{config.description(inventory.id)}</p>
              {config.warning && (
                <Banner status={config.destructive ? 'critical' : 'warning'} title={config.warning} />
              )}
            </TextContainer>
          </Modal.Section>
        </Modal>
      )}

      <PDFModal
        title={'INVENTARIO'}
        subtitle={inventory ? dateToHumanReadableFormat(new Date()) + '\n \n' + inventory.deposit.name : null}
        active={activePDFModal}
        onClose={() => setActivePDFModal(false)}
        domain={'inventories'}
        model={'stock'}
        query={queryPDF}
        attributesDefault={{ cover: true, showNumbers: true, empty: false, blink: false }}
        AttributesWrapper={PDFAttributes}
      />
    </LegacyStack>
  );
};

const PDFAttributes = ({ attr, onAttrChange = () => {} }) => {
  const [attributes, setAttributes] = useState(attr);
  const [optionSelected, setOptionSelected] = useState('0');

  const handleOptionChange = (selected) => {
    setOptionSelected(selected);

    switch (selected) {
      case '0':
        setAttributes({ ...attr, showNumbers: true, empty: false, blink: false });
        break;
      case '1':
        setAttributes({ ...attr, showNumbers: false, empty: true, blink: false });
        break;
      case '2':
        setAttributes({ ...attr, showNumbers: false, empty: true, blink: true });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    handleOptionChange('0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onAttrChange(attributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  return (
    <>
      <LegacyStack spacing="loose">
        <LegacyStack.Item>
          <>
            <Text variant="bodyMd">Propiedades</Text>
            <Button
              primary={attributes.cover}
              onClick={() => setAttributes({ ...attributes, cover: !attributes.cover })}
            >
              Incluir portada
            </Button>
          </>
        </LegacyStack.Item>
        <LegacyStack.Item>
          <Text variant="bodyMd">Tipo de tabla</Text>
          <ButtonGroup segmented>
            <Button onClick={() => handleOptionChange('0')} primary={optionSelected === '0'}>
              Resumen
            </Button>
            <Button onClick={() => handleOptionChange('1')} primary={optionSelected === '1'}>
              Revisión
            </Button>
            <Button onClick={() => handleOptionChange('2')} primary={optionSelected === '2'}>
              Ciego
            </Button>
          </ButtonGroup>
        </LegacyStack.Item>
      </LegacyStack>
    </>
  );
};

export default ViewInventoryPrimaryActions;
